import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit, startAfter, orderBy, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { getAllWalletUsers } from '../../services/walletService';

// FilterButtons komponens
const FilterButtons = ({ filter, setFilter, showAll, setShowAll }) => {
  return (
    <div className="p-4 border-b border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              filter === 'all'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Összes
          </button>
          <button
            onClick={() => setFilter('connected')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              filter === 'connected'
                ? 'bg-green-100 text-green-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Kapcsolt
          </button>
          <button
            onClick={() => setFilter('disconnected')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              filter === 'disconnected'
                ? 'bg-red-100 text-red-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Nem kapcsolt
          </button>
        </div>
        
        <div className="flex items-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
              showAll
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showAll ? (
              <>
                <Icons.List className="w-4 h-4 mr-2" />
                Lapozás visszaállítása
              </>
            ) : (
              <>
                <Icons.ListFilter className="w-4 h-4 mr-2" />
                Összes megjelenítése
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// SearchBar komponens
const SearchBar = ({ searchTerm, setSearchTerm, onSearch }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <div className="p-4 border-b border-gray-200 bg-gray-50">
      <div className="flex items-center">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icons.Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Keresés név, email vagy azonosító alapján..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <button
          onClick={onSearch}
          className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Keresés
        </button>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              onSearch();
            }}
            className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Icons.X className="h-4 w-4 mr-1" />
            Törlés
          </button>
        )}
      </div>
    </div>
  );
};

// LoadingState komponens
const LoadingState = () => (
  <div className="flex justify-center items-center p-8">
    <Icons.Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
  </div>
);

// ErrorState komponens
const ErrorState = ({ error }) => (
  <div className="p-4 text-red-700 bg-red-100 rounded-md m-4">
    <p>{error}</p>
  </div>
);

// EmptyState komponens
const EmptyState = () => (
  <div className="p-8 text-center">
    <Icons.FileX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">Nincs találat</h3>
    <p className="text-gray-500">
      Nem található felhasználó a megadott szűrési feltételekkel.
    </p>
  </div>
);

// WalletUserTable komponens - frissítve, hogy kezelje a különböző forrásból származó adatokat
const WalletUserTable = ({ users, formatDate, onCreateConnection }) => {
  // Egyenleg formázása
  const formatBalance = (balance) => {
    if (balance === undefined || balance === null) return 'N/A';
    return new Intl.NumberFormat('hu-HU').format(balance) + ' pont';
  };

  // FLCV egyenleg formázása
  const formatFLCVBalance = (balance) => {
    if (balance === undefined || balance === null) return 'N/A';
    return new Intl.NumberFormat('hu-HU').format(balance) + ' FLCV';
  };

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Felhasználó
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Wallet Adatok
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Egyenlegek
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Utolsó Tranzakció
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Előny Klub Kapcsolat
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Customer Kapcsolat
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Forrás
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Kapcsolat Ideje
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Műveletek
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {users.map((user) => {
          // Egyenleg meghatározása a forrás alapján
          const balance = 
            user.balance !== undefined ? user.balance :
            user.walletBalance !== undefined ? user.walletBalance :
            user.walletData?.balance !== undefined ? user.walletData.balance :
            null;
            
          // Összes egyenleg összegyűjtése
          const allBalances = {};
          
          // Pont egyenleg
          if (balance !== null && balance !== undefined) {
            allBalances['PONT'] = balance;
          }
          
          // FLCV és más egyenlegek a balances objektumból
          if (user.balances) {
            Object.entries(user.balances).forEach(([key, value]) => {
              allBalances[key] = value;
            });
          }
          
          // Egyenlegek a walletData-ból
          if (user.walletData?.balances) {
            Object.entries(user.walletData.balances).forEach(([key, value]) => {
              allBalances[key] = value;
            });
          }
          
          // Egyenlegek a details-ből (WalletUserManager formátum)
          if (user.details?.balances) {
            Object.entries(user.details.balances).forEach(([key, value]) => {
              allBalances[key] = value;
            });
          }

          // Utolsó tranzakció adatai
          const lastTransaction = user.activity ? user.activity[0] : null;
            
          return (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {user.source === 'customers' 
                  ? (user.primaryContact?.email || 'N/A') 
                  : (user.email || 'N/A')}
                {user.name && <div className="text-xs text-gray-500">Név: {user.name}</div>}
                {user.displayName && <div className="text-xs text-gray-500">Név: {user.displayName}</div>}
                {user.first_name && <div className="text-xs text-gray-500">Név: {user.first_name} {user.last_name}</div>}
                {user.details?.first_name && <div className="text-xs text-gray-500">Név: {user.details.first_name} {user.details.last_name}</div>}
                <div className="text-xs text-gray-500">ID: {user.id || user.user_id}</div>
                {user.imported_at && <div className="text-xs text-gray-500">Importálva: {formatDate(user.imported_at)}</div>}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.walletId ? (
                  <div>
                    <div>ID: {user.walletId}</div>
                    {user.walletData && user.walletData.email && (
                      <div className="text-xs text-gray-500">Email: {user.walletData.email}</div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400">Nincs wallet</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {Object.keys(allBalances).length > 0 ? (
                  <div className="space-y-1.5">
                    {Object.entries(allBalances).map(([currency, amount]) => (
                      <div key={currency} className="flex items-center">
                        <span className={`font-medium ${Number(amount) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {new Intl.NumberFormat('hu-HU').format(amount)} {currency}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-400">Nincs egyenleg</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {lastTransaction ? (
                  <div>
                    <div className="font-medium">{lastTransaction.activity}</div>
                    <div className="text-xs">{lastTransaction.amount}</div>
                    <div className="text-xs">Dátum: {lastTransaction.date}</div>
                    <div className="text-xs">Státusz: {lastTransaction.status}</div>
                    {lastTransaction.txid && <div className="text-xs">TXID: {lastTransaction.txid}</div>}
                  </div>
                ) : (
                  <span className="text-gray-400">Nincs tranzakció</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.elonyKlubConnected || user.source === 'users' ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Kapcsolva
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                    Nincs kapcsolva
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.customerConnected || user.source === 'customers' ? (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    Kapcsolva
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                    Nincs kapcsolva
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full 
                  ${user.source === 'walletUsers' ? 'bg-purple-100 text-purple-800' : 
                    user.source === 'customers' ? 'bg-blue-100 text-blue-800' : 
                    'bg-indigo-100 text-indigo-800'}`}>
                  {user.source === 'walletUsers' ? 'Wallet' : 
                   user.source === 'customers' ? 'Ügyfél' : 'Előny Klub'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(user.elonyKlubConnectedAt || user.walletConnectedAt || user.customerConnectedAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  onClick={() => onCreateConnection(user)}
                  className="text-indigo-600 hover:text-indigo-900 flex items-center"
                >
                  <Icons.Link className="w-4 h-4 mr-1" />
                  <span>Kapcsolat</span>
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// TableHeader komponens
const TableHeader = ({ users, totalCount, showAll, page, hasMore }) => (
  <div className="p-4 bg-gray-50 border-b border-gray-200">
    <p className="text-sm text-gray-600">
      {showAll 
        ? `Összes rekord megjelenítése (${users.length}${hasMore ? '+ további rekordok' : ''})`
        : `${users.length} rekord megjelenítése${hasMore ? ' (további rekordok elérhetők)' : ''} (${page}. oldal)`
      }
    </p>
    {hasMore && (
      <p className="text-sm text-yellow-600 mt-1">
        <Icons.AlertTriangle className="w-4 h-4 inline mr-1" />
        Figyelem: Nem minden rekord jelenik meg. Használj szűrőket vagy lapozást a további rekordok megtekintéséhez.
      </p>
    )}
  </div>
);

// LoadMoreButton komponens
const LoadMoreButton = ({ loadMore, loading }) => (
  <div className="p-4 border-t border-gray-200 flex justify-center">
    <button
      onClick={loadMore}
      disabled={loading}
      className="px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
    >
      {loading ? (
        <Icons.Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        'További betöltése'
      )}
    </button>
  </div>
);

// ConnectionModal komponens
const ConnectionModal = ({ isOpen, onClose, selectedUser, onConnect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [connectionType, setConnectionType] = useState('elonyKlub'); // 'elonyKlub' vagy 'customer'

  useEffect(() => {
    if (searchTerm.length >= 3) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, connectionType]);

  const searchUsers = async () => {
    if (searchTerm.length < 3) return;
    
    setLoading(true);
    try {
      // Keresés a megfelelő gyűjteményben a kapcsolat típusa alapján
      const collectionName = connectionType === 'elonyKlub' ? 'users' : 'customers';
      
      const usersRef = collection(db, collectionName);
      const q = query(
        usersRef,
        where('email', '>=', searchTerm.toLowerCase()),
        where('email', '<=', searchTerm.toLowerCase() + '\uf8ff'),
        limit(10)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setSearchResults([]);
      } else {
        const results = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          source: connectionType === 'elonyKlub' ? 'users' : 'customers'
        }));
        setSearchResults(results);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedTarget) return;
    
    await onConnect(selectedUser, selectedTarget, connectionType);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full">
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-xl font-medium text-gray-900">Kapcsolat létrehozása</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <Icons.X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Kiválasztott felhasználó:</h4>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium">{selectedUser.email || selectedUser.primaryContact?.email}</p>
            <p className="text-xs text-gray-500">ID: {selectedUser.id || selectedUser.user_id}</p>
            <p className="text-xs text-gray-500">Forrás: {
              selectedUser.source === 'walletUsers' ? 'Wallet' : 
              selectedUser.source === 'customers' ? 'Ügyfél' : 'Előny Klub'
            }</p>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kapcsolat típusa
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="connectionType"
                value="elonyKlub"
                checked={connectionType === 'elonyKlub'}
                onChange={() => setConnectionType('elonyKlub')}
              />
              <span className="ml-2">Előny Klub felhasználó</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="connectionType"
                value="customer"
                checked={connectionType === 'customer'}
                onChange={() => setConnectionType('customer')}
              />
              <span className="ml-2">Customer</span>
            </label>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Keresés {connectionType === 'elonyKlub' ? 'Előny Klub felhasználó' : 'Customer'} alapján
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Email cím..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
            {loading && (
              <div className="absolute right-3 top-2.5">
                <Icons.Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
              </div>
            )}
          </div>
        </div>

        {searchResults.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Találatok:</h4>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {searchResults.map(result => (
                <div
                  key={result.id}
                  className={`p-4 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    selectedTarget?.id === result.id ? 'bg-indigo-50' : ''
                  }`}
                  onClick={() => setSelectedTarget(result)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{result.email || result.primaryContact?.email}</p>
                      <p className="text-xs text-gray-500">ID: {result.id}</p>
                    </div>
                    {selectedTarget?.id === result.id && (
                      <Icons.Check className="h-5 w-5 text-indigo-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Mégsem
          </button>
          <button
            onClick={handleConnect}
            disabled={!selectedTarget}
            className={`px-4 py-2 rounded-md text-white ${
              selectedTarget
                ? 'bg-indigo-600 hover:bg-indigo-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Kapcsolat létrehozása
          </button>
        </div>
      </div>
    </div>
  );
};

// Fő komponens
const WalletConnectionsPage = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const pageSize = 20;

  useEffect(() => {
    fetchAllUsers();
  }, [filter, showAll, searchTerm]);

  // Keresés indítása - késleltetett keresés a túl gyakori API hívások elkerülése érdekében
  const handleSearch = () => {
    // Már nincs szükség külön fetchAllUsers hívásra, mert a useEffect figyeli a searchTerm változását
    console.log("Keresés indítása:", searchTerm);
  };

  // Új függvény az összes felhasználó lekérdezéséhez
  const fetchAllUsers = async (loadMore = false) => {
    try {
      setLoading(true);
      setError(null);

      // Ha a "Mutasd az összeset" be van kapcsolva, akkor nagy méretű lekérdezést használunk
      const effectivePageSize = showAll ? 10000 : pageSize;
      
      console.log(`Fetching all users with filter: ${filter}, showAll: ${showAll}, pageSize: ${effectivePageSize}, searchTerm: ${searchTerm}`);
      
      // Mindig lekérjük az összes felhasználót, de a filter alapján szűrjük a megjelenítést
      let connected = undefined;
      if (filter === 'connected') {
        connected = true;
      } else if (filter === 'disconnected') {
        connected = false;
      }

      // Ha új lekérdezés (nem "load more"), akkor nullázzuk a lastVisible-t
      if (!loadMore) {
        setLastVisible(null);
        setAllUsers([]);
        setPage(1);
      }

      // Wallet felhasználók lekérdezése
      const walletUsersResult = await getAllWalletUsers({
        connected: connected,
        pageSize: effectivePageSize,
        startAfter: loadMore ? lastVisible : null
      });
      
      // Wallet felhasználók forrás megjelölése
      const walletUsersWithSource = walletUsersResult.users.map(user => ({
        ...user,
        source: 'walletUsers'
      }));

      // Customers lekérdezése
      let customersData = [];
      try {
        const customersQuery = query(
          collection(db, 'customers'),
          limit(effectivePageSize)
        );
        const customersSnapshot = await getDocs(customersQuery);
        
        if (!customersSnapshot.empty) {
          customersData = customersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'customers'
          }));
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }

      // Users lekérdezése
      let usersData = [];
      try {
        const usersQuery = query(
          collection(db, 'users'),
          limit(effectivePageSize)
        );
        const usersSnapshot = await getDocs(usersQuery);
        
        if (!usersSnapshot.empty) {
          usersData = usersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'users'
          }));
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }

      // Adatok összefűzése és deduplikálása
      const allUsersData = [...walletUsersWithSource, ...customersData, ...usersData];
      
      // Szűrés a filter alapján, ha szükséges
      let filteredUsers = allUsersData;
      if (filter === 'connected') {
        filteredUsers = allUsersData.filter(user => 
          user.walletConnected || 
          user.elonyKlubConnected || 
          user.customerConnected || 
          user.source === 'walletUsers'
        );
      } else if (filter === 'disconnected') {
        filteredUsers = allUsersData.filter(user => 
          !(user.walletConnected || 
            user.elonyKlubConnected || 
            user.customerConnected || 
            user.source === 'walletUsers')
        );
      }

      // Keresési kifejezés alapján szűrés, ha van megadva
      if (searchTerm.trim() !== '') {
        const searchTermLower = searchTerm.toLowerCase().trim();
        filteredUsers = filteredUsers.filter(user => {
          // Keresés név, email és azonosító alapján
          const nameMatch = 
            (user.name && user.name.toLowerCase().includes(searchTermLower)) ||
            (user.displayName && user.displayName.toLowerCase().includes(searchTermLower)) ||
            (user.first_name && user.first_name.toLowerCase().includes(searchTermLower)) ||
            (user.last_name && user.last_name.toLowerCase().includes(searchTermLower));
          
          const emailMatch = 
            (user.email && user.email.toLowerCase().includes(searchTermLower)) ||
            (user.primaryContact?.email && user.primaryContact.email.toLowerCase().includes(searchTermLower)) ||
            (user.walletData?.email && user.walletData.email.toLowerCase().includes(searchTermLower));
          
          const idMatch = 
            (user.id && user.id.toString().includes(searchTermLower)) ||
            (user.user_id && user.user_id.toString().includes(searchTermLower)) ||
            (user.walletId && user.walletId.toString().includes(searchTermLower));
          
          return nameMatch || emailMatch || idMatch;
        });
      }

      // Eredmények beállítása
      setAllUsers(prev => loadMore ? [...prev, ...filteredUsers] : filteredUsers);
      setLastVisible(walletUsersResult.lastVisible);
      
      // Beállítjuk, hogy van-e még több rekord
      const hasMoreRecords = walletUsersResult.users.length === effectivePageSize || 
                            customersData.length === effectivePageSize || 
                            usersData.length === effectivePageSize;
      setHasMore(hasMoreRecords);
      
      if (loadMore) {
        setPage(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      console.error('Error details:', err.message, err.stack);
      setError(`Hiba történt a felhasználók lekérése során: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      fetchAllUsers(true);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp instanceof Date 
      ? timestamp 
      : new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
    
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Kapcsolat létrehozása
  const handleCreateConnection = async (sourceUser, targetUser, connectionType) => {
    try {
      // Ellenőrizzük, hogy a forrás felhasználó wallet felhasználó-e
      if (sourceUser.source !== 'walletUsers') {
        throw new Error('A forrás felhasználónak wallet felhasználónak kell lennie');
      }

      // Ellenőrizzük, hogy a cél felhasználó a megfelelő típusú-e
      if (connectionType === 'elonyKlub' && targetUser.source !== 'users') {
        throw new Error('A cél felhasználónak Előny Klub felhasználónak kell lennie');
      } else if (connectionType === 'customer' && targetUser.source !== 'customers') {
        throw new Error('A cél felhasználónak Customer-nek kell lennie');
      }

      // Frissítjük a wallet felhasználót a kapcsolattal
      const walletUserRef = doc(db, 'walletUsers', sourceUser.id);
      
      const updateData = {};
      
      if (connectionType === 'elonyKlub') {
        updateData.elonyKlubConnected = true;
        updateData.elonyKlubConnectedAt = new Date();
        updateData.elonyKlubUserId = targetUser.id;
      } else {
        updateData.customerConnected = true;
        updateData.customerConnectedAt = new Date();
        updateData.customerId = targetUser.id;
      }
      
      await updateDoc(walletUserRef, updateData);

      // Frissítjük a cél felhasználót is a kapcsolattal
      const targetRef = doc(db, connectionType === 'elonyKlub' ? 'users' : 'customers', targetUser.id);
      
      const targetUpdateData = {
        walletConnected: true,
        walletConnectedAt: new Date(),
        walletId: sourceUser.id
      };
      
      await updateDoc(targetRef, targetUpdateData);

      // Frissítjük a felhasználók listáját
      fetchAllUsers();

      // Sikeres üzenet
      alert(`Kapcsolat sikeresen létrehozva a wallet felhasználó és a ${connectionType === 'elonyKlub' ? 'Előny Klub felhasználó' : 'Customer'} között.`);
    } catch (error) {
      console.error('Error creating connection:', error);
      alert(`Hiba történt a kapcsolat létrehozása során: ${error.message}`);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wallet Kapcsolatok</h1>
        <p className="mt-1 text-sm text-gray-500">
          Felhasználók, ügyfelek és wallet kapcsolataik kezelése
        </p>
      </div>

      {/* Információs üzenet a háttérben történő importálásról */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-md">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icons.Info className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Folyamatos importálás</h3>
            <div className="mt-1 text-sm text-blue-700">
              <p>A tárcák importálása folyamatosan, a háttérben történik. Az új tárcák automatikusan megjelennek a listában, amint elérhetővé válnak.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <FilterButtons 
          filter={filter} 
          setFilter={setFilter} 
          showAll={showAll} 
          setShowAll={setShowAll} 
        />
        
        <SearchBar 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
          onSearch={handleSearch} 
        />

        <div className="overflow-x-auto">
          {loading && allUsers.length === 0 ? (
            <LoadingState />
          ) : error ? (
            <ErrorState error={error} />
          ) : allUsers.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <TableHeader 
                users={allUsers} 
                totalCount={totalCount} 
                showAll={showAll} 
                page={page}
                hasMore={hasMore}
              />
              
              <WalletUserTable 
                users={allUsers} 
                formatDate={formatDate} 
                onCreateConnection={(user) => {
                  setSelectedUser(user);
                  setShowConnectionModal(true);
                }}
              />
              
              {!showAll && hasMore && (
                <LoadMoreButton loadMore={loadMore} loading={loading} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Kapcsolat létrehozása modális */}
      {showConnectionModal && selectedUser && (
        <ConnectionModal
          isOpen={showConnectionModal}
          onClose={() => {
            setShowConnectionModal(false);
            setSelectedUser(null);
          }}
          selectedUser={selectedUser}
          onConnect={handleCreateConnection}
        />
      )}
    </div>
  );
};

export default WalletConnectionsPage; 